import { Fragment, type ReactNode } from "react"
import { Text, VStack } from "@chakra-ui/react"
import { BaseDataConnectionCard } from "@hydrologiq/shared/data-connections/BaseDataConnectionCard"
import { ConnectionStatus } from "@hydrologiq/shared/data-connections/constants"
import { ScopeLinkChip } from "@hydrologiq/shared/components/ScopeLinkChip"
import { ManagedByChip } from "@hydrologiq/shared/data-connections/ManagedByChip"
import type { DataConnection, ParentGroup } from "../../utils/groupConnections"

export interface ParentGroupCardProps {
  group: ParentGroup
  /** Renders one module's connection inside the group. */
  renderConnection?: (connection: DataConnection) => ReactNode
}

/**
 * An Asset Group in the system Data Connections view, drawn like any other
 * asset-held connection: lock, expander, chip and name, connector, status.
 *
 * A group's connections live on its modules and are managed on the parent
 * asset, never from the system, so there is no toggle. Expanding it shows
 * each module's connection read-only.
 */
export function ParentGroupCard({ group, renderConnection }: ParentGroupCardProps) {
  const moduleConnections = group.connections.filter((c) => c.assetId !== group.parentAssetId)
  const connectorNames = [...new Set(moduleConnections.map((c) => c.connectorName || c.connectorType))]
  const externalIds = [...new Set(moduleConnections.map((c) => c.externalId).filter(Boolean))]
  const moduleCount = group.children.length

  // The group's header stands in for its modules' connections
  const summary = {
    connectorType: moduleConnections[0]?.connectorType ?? "",
    connectorName: connectorNames.join(", "),
    externalId: externalIds.length === 1 ? externalIds[0] : null,
    status: group.activeCount > 0 ? ConnectionStatus.Active : ConnectionStatus.Inactive,
  }

  return (
    <BaseDataConnectionCard
      connection={summary}
      defaultExpanded={false}
      title={group.parentAssetName}
      chips={
        <ScopeLinkChip
          label="Asset"
          href={`/assets/${group.parentAssetId}/settings?tab=connections`}
          entityName={group.parentAssetName}
        />
      }
      headerNote={
        <ManagedByChip
          scope="asset"
          detail={`${moduleCount} module${moduleCount === 1 ? "" : "s"}, ${group.activeCount} active`}
        />
      }
    >
      <VStack align="stretch" gap={3}>
        {group.children.map((child) => {
          const connection = moduleConnections.find((c) => c.assetId === child.assetId)
          return connection && renderConnection ? (
            <Fragment key={child.assetId}>{renderConnection(connection)}</Fragment>
          ) : (
            <Text key={child.assetId} fontSize="sm" color="fg.muted">
              {child.assetName}: no data connection configured.
            </Text>
          )
        })}
      </VStack>
    </BaseDataConnectionCard>
  )
}

export default ParentGroupCard
